import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react';
import SVG from 'react-inlinesvg';
import classNames from './ChatScroller.module.scss';
import classes from 'classnames';
import downArrow from '@/assets/svg/down-arrow-long.svg';

interface ChatScrollerProps {
  children: React.ReactNode;
  className?: string;
  enableDragToScroll?: boolean;
}

export interface ChatScrollerRef {
  scrollToBottom: () => void;
  scrollToTop: () => void;
  scrollDelta: (delta: number) => void;
}

const ChatScroller = forwardRef<ChatScrollerRef, ChatScrollerProps>(
  ({ children, className, enableDragToScroll = true, ...props }, ref) => {
    const scrollContentRef = useRef<HTMLDivElement>(null);
    const scrollWrapperRef = useRef<HTMLDivElement>(null);
    const scrollInnerDivRef = useRef<HTMLDivElement>(null);
    const startY = useRef(0);
    const scrollTop = useRef(0);
    const [showScrollBtn, setShowScrollBtn] = useState(false);

    useImperativeHandle(ref, () => {
      return {
        scrollToBottom: () => {
          if (scrollContentRef.current) {
            scrollContentRef.current.scroll({
              top: scrollContentRef.current.scrollHeight,
              behavior: 'smooth',
            });
          }
        },
        scrollToTop: () => {
          if (scrollContentRef.current) {
            scrollContentRef.current.scroll({
              top: 0,
              behavior: 'smooth',
            });
          }
        },
        scrollDelta: (delta: number) => {
          if (scrollContentRef.current) {
            scrollContentRef.current.scroll({
              top: scrollContentRef.current.scrollTop + delta,
              behavior: 'smooth',
            });
          }
        },
      };
    }, []);

    useEffect(() => {
      const scrollInnerDivElement = scrollInnerDivRef.current;
      const scrollWrapperElement = scrollWrapperRef.current;

      if (scrollInnerDivElement && scrollWrapperElement) {
        const scrollbarWidth = scrollWrapperElement.offsetWidth - scrollInnerDivElement.offsetWidth;
        scrollWrapperElement.style.setProperty('--scrollbar-width-offset', `${scrollbarWidth}px`);
      }
    }, []);

    useEffect(() => {
      const el = scrollContentRef.current;
      if (!el) return;

      const checkScroll = () => {
        setShowScrollBtn(el.scrollTop + el.clientHeight < el.scrollHeight - 50);
      };

      el.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(el);

      return () => {
        el.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }, []);

    const isTouchDevice = useCallback(() => {
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator as any).msMaxTouchPoints > 0
      );
    }, []);

    useEffect(() => {
      const scrollContentElement = scrollContentRef.current;

      if (!scrollContentElement || !enableDragToScroll) {
        return;
      }

      if (isTouchDevice()) {
        const handleTouchMove = (e: TouchEvent) => {
          e.stopPropagation();
        };
        scrollContentElement.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
          scrollContentElement.removeEventListener('touchmove', handleTouchMove);
        };
      }

      const handleMouseDown = (e: MouseEvent) => {
        startY.current = e.pageY;
        scrollTop.current = scrollContentElement.scrollTop;
        scrollContentElement.addEventListener('mousemove', handleMouseMove);
        scrollContentElement.addEventListener('mouseleave', handleMouseUp);
        document.addEventListener('mouseup', handleMouseUp);
      };

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        const walk = e.pageY - startY.current;
        scrollContentElement.scrollTop = scrollTop.current - walk;
        if (walk <= -10 || 10 <= walk) {
          scrollContentElement.classList.add(classNames.isDragging);
        }
      };

      const handleMouseUp = () => {
        scrollContentElement.removeEventListener('mousemove', handleMouseMove);
        scrollContentElement.removeEventListener('mouseleave', handleMouseUp);
        document.removeEventListener('mouseup', handleMouseUp);
        scrollContentElement.classList.remove(classNames.isDragging);
      };

      scrollContentElement.addEventListener('mousedown', handleMouseDown);

      return () => {
        scrollContentElement.removeEventListener('mousedown', handleMouseDown);
        handleMouseUp();
      };
    }, [enableDragToScroll, isTouchDevice]);

    return (
      <div
        className={classes(classNames.scrollContainerWrapper, {
          [className || '']: className,
        })}
        {...props}
        ref={scrollWrapperRef}
      >
        <div
          className={classes(classNames.scrollContent, classNames.hideScrollbar)}
          ref={scrollContentRef}
          data-contaner='ChatScrollerContent'
        >
          <div ref={scrollInnerDivRef}></div>
          {children}
        </div>
        {showScrollBtn && (
          <button
            className={classNames.scrollDownBtn}
            onClick={() =>
              scrollContentRef.current?.scroll({
                top: scrollContentRef.current.scrollHeight,
                behavior: 'smooth',
              })
            }
            aria-label='Scroll to bottom'
          >
            <SVG src={downArrow} />
          </button>
        )}
      </div>
    );
  }
);

export default ChatScroller;
